import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {injectIntl} from 'react-intl';

import {ENTITY_TYPE_VENUE} from '@stubhub/entity-helper';
import DateFilter from '@stubhub/react-date-filter';
import GeoFilter from '@stubhub/react-geo-filter';
import PillBar from '@stubhub/react-pill-bar';
import {theme} from '@stubhub/react-theme-provider';

import {messages} from '../../apps/entity/messages';
import {themeBackgroundColor} from '../../helpers/theming-helper';
import {DISPLAY_TYPE_CALENDAR, DISPLAY_TYPE_EVENTS_ONLY} from '../../utils/constants';
import EventSorter from '../react-event-sorter';
import {sortValues} from '../react-event-sorter/messages';

import './index.scss';

const DEFAULT_INNER_WIDTH = 600;
const SMALL_WIDTH_LIMIT = 640;
const DEFAULT_TIMEOUT = 100;

class EntityPillBar extends React.Component {
  static contextTypes = {
    globalRegistry: PropTypes.object,
  };

  constructor(props, context) {
    super(props, context);
    const {options = {}, intl} = props;
    const tabsConfig = {};
    const defaultTabName = DISPLAY_TYPE_EVENTS_ONLY;
    const tabNames = Object.keys(tabsConfig);

    const {globalRegistry = {}} = context;

    // Event sorter options
    this.sortOptions = this.translateOptions(intl);

    /**
     * Default State for the Tabs
     *
     */
    this.state = {
      selectedTabName: defaultTabName,
      tabNames,
      hasShadow: false,
    };

    if (options.pbC) {
      this.state.selectedPanel = options.pbC === 'c' ? 'list' : 'calendar';
    }

    if (typeof globalRegistry.getFeatureValue === 'function') {
      this.hideTabsConfig = globalRegistry.getFeatureValue('common.hideTabsConfigOnCategoryGroupingEntity');
    }

    this.isThrottled = false;
    this.handleScroll = this.handleScroll.bind(this);
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  handleScroll() {
    if (this.isThrottled) {
      return;
    }

    this.isThrottled = true;

    setTimeout(() => {
      const pillBar = document.querySelector('.PillBar__Container');
      if (pillBar) {
        const pillBarRect = pillBar.getBoundingClientRect();
        const hasShadow = pillBarRect.top === 0;
        const {hasShadow: currentHasShadow} = this.state;

        if (currentHasShadow !== hasShadow) {
          this.setState({hasShadow});
        }
      }
      this.isThrottled = false;
    }, DEFAULT_TIMEOUT);
  }

  translateOptions(intl) {
    const sortOptions = [];
    for (const prop in sortValues) {
      sortOptions.push({key: prop, value: sortValues[prop]});
    }
    const opts = sortOptions.map((s) => {
      const _s = {...s};
      _s.name = intl.formatMessage(messages[_s.key]);

      return _s;
    });

    return opts;
  }

  /**
   * Callback Method used by the sort filter to trigger the call to Search Catalog Event API with selected search specs
   * @param option [object] The object containing the `sort option` from the component.
   *
   */
  sort = (option) => {
    const {
      filterActions: {setSort},
    } = this.props;
    setSort(option);
  };

  /**
   * Function to handle the Tabs change click handler
   *
   */
  onTabClickHandler = (selectedTabName) => {
    const {selectedTabName: selectedTabNameState} = this.state;
    const {filterActions} = this.props;
    this.setState({selectedPanel: selectedTabName === DISPLAY_TYPE_EVENTS_ONLY ? DISPLAY_TYPE_CALENDAR : undefined});
    if (selectedTabName !== selectedTabNameState) {
      const {onTabChange} = filterActions;
      this.setState({selectedTabName});
      onTabChange && onTabChange(selectedTabName);
    }
  };

  isSmall = () => {
    const windowWidth = __CLIENT__ ? window.innerWidth : DEFAULT_INNER_WIDTH;

    return windowWidth < SMALL_WIDTH_LIMIT;
  };

  getSelectedSort = (options) => {
    let selectedSort = 'date';

    if (!options || !options.pbS) {
      return selectedSort;
    }
    selectedSort =
      options.pbS === 'd'
        ? 'date'
        : options.pbS === 'i'
        ? 'distance'
        : options.pbS === 'p'
        ? 'price'
        : options.pbS === 'o'
        ? 'popularity'
        : selectedSort;

    return selectedSort;
  };

  pillBarLayout(layoutParams) {
    const {config, tabParams, pillControls, entityType} = layoutParams;
    const tabsControl = tabParams.tabsControl ? tabParams.tabsControl : {itemType: 'tabs', tabParams};
    const {filterSection, geoFilter, eventSorter} = pillControls;
    const miniGeoFilterControl = {control: geoFilter, noLabel: true};

    const defaultLeftSection = [];
    if (entityType === ENTITY_TYPE_VENUE) {
      defaultLeftSection.push(filterSection, tabsControl);
    } else {
      defaultLeftSection.push(miniGeoFilterControl, filterSection, tabsControl);
    }

    const layout = {
      Default: {
        leftSection: defaultLeftSection,
        rightSection: [eventSorter],
      },
      Sports: {
        leftSection: [miniGeoFilterControl, filterSection, tabsControl],
        rightSection: [eventSorter],
      },
      SportsPartner: {
        leftSection: [tabsControl],
        rightSection: [],
      },
      UniquePartner: {
        leftSection: [tabsControl],
        rightSection: [],
      },
      CategoryGrouping: {
        leftSection: this.hideTabsConfig ? [] : [miniGeoFilterControl, filterSection, tabsControl],
        rightSection: [eventSorter],
      },
      SportsPartnerGrouping: {
        leftSection: this.hideTabsConfig ? [] : [miniGeoFilterControl, filterSection, tabsControl],
        rightSection: [eventSorter],
      },
      City: {
        leftSection: [filterSection],
        rightSection: [],
      },
      Geography: {
        leftSection: [],
        rightSection: [],
      },
      Search: {
        leftSection: [miniGeoFilterControl, filterSection, tabsControl],
        rightSection: [eventSorter],
      },
    };

    return layout[config];
  }

  getControls(params) {
    const {options = {}, config, sortParams, noDateFilter} = params;
    const {sortOptions, start, end} = sortParams;
    const backgroundColor = null;
    const isSmall = this.isSmall();
    const selectedSort = this.getSelectedSort(options);

    return {
      filterSection: noDateFilter ? null : (
        <DateFilter
          start={start}
          end={end}
          iconColor={backgroundColor}
          inPillBar
          useAltMsgs
          draggable={isSmall}
          useBackdrop={isSmall}
        />
      ),
      geoFilter:
        config !== 'City' ? (
          <GeoFilter
            className="filter-bar-item geo-filter-pill"
            inPillBar
            draggable={isSmall}
            useBackdrop={isSmall}
            dropdownAlign="left"
            dropDownToggle={false}
            showDraggableTitle
          />
        ) : null,
      eventSorter:
        config !== 'City' ? (
          <EventSorter
            draggable={isSmall}
            useBackdrop={isSmall}
            items={sortOptions}
            selected={selectedSort}
            onSelection={(option) => this.sort(option)}
          />
        ) : null,
    };
  }

  getTabSortParams(params) {
    const {filterActions, themeData} = params;
    const {onTabChange, setSort, setSearch} = filterActions;
    const tabsConfig = {};
    const defaultTabName = DISPLAY_TYPE_EVENTS_ONLY;
    const tabParams = {...themeData, tabsConfig, onTabChange, defaultTabName, cojoinedTabs: true};
    const sortParams = {sortOptions: this.sortOptions, setSort, setSearch};

    return {tabParams, sortParams};
  }

  render() {
    const {options = {}, config, theme, themeProvider, noDateFilter, isArtist, entityType} = this.props;
    let {tabParams, sortParams} = this.getTabSortParams(this.props);
    const {tabNames, selectedTabName, selectedPanel, hasShadow} = this.state;
    tabParams = {
      ...tabParams,
      tabNames,
      selectedTabName,
      themeBackgroundColor,
      theme,
      themeProvider,
      onTabClickHandler: this.onTabClickHandler,
    };
    sortParams = {...sortParams, theme, themeProvider};
    const pillControls = this.getControls({
      options,
      config,
      selectedPanel,
      sortParams,
      noDateFilter,
      isArtist,
    });
    const layout = this.pillBarLayout({config, tabParams, pillControls, entityType});

    return (
      <PillBar
        className={cx('Entity_PillBar', {Entity_PillBar_Shadow: hasShadow})}
        leftSection={layout.leftSection}
        rightSection={layout.rightSection}
      />
    );
  }
}

EntityPillBar.propTypes = {
  config: PropTypes.string.isRequired,
  options: PropTypes.object,
  filterActions: PropTypes.object.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  themeData: PropTypes.object,

  /** Is date filter disabled? */
  noDateFilter: PropTypes.bool,

  /** The intl object*/
  intl: PropTypes.object.isRequired,

  /** Current Global theme object */
  theme: PropTypes.object,

  /** ThemeProvider object for applying the theme */
  themeProvider: PropTypes.func,

  /** Is artist? */
  isArtist: PropTypes.bool,

  /** Entity type */
  entityType: PropTypes.string,
};

EntityPillBar.defaultProps = {
  config: 'default',
};

export default injectIntl(theme(EntityPillBar));
